import { useState } from "react";
import { X, FileText, FileDown, Code, Clipboard, Check, Download } from "lucide-react";

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  content: string;
  title?: string;
}

type ExportFormat = "pdf" | "docx" | "markdown" | "clipboard";

interface FormatOption {
  id: ExportFormat;
  label: string;
  description: string;
  icon: React.ElementType;
  ext: string;
}

const formats: FormatOption[] = [
  { id: "pdf", label: "PDF Document", description: "Best for sharing and printing", icon: FileDown, ext: ".pdf" },
  { id: "docx", label: "Word Document", description: "Editable in Microsoft Word", icon: FileText, ext: ".docx" },
  { id: "markdown", label: "Markdown", description: "Plain text with formatting", icon: Code, ext: ".md" },
  { id: "clipboard", label: "Copy to Clipboard", description: "Paste anywhere instantly", icon: Clipboard, ext: "" },
];

export function ExportModal({ isOpen, onClose, content, title = "Export" }: ExportModalProps) {
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>("pdf");
  const [copied, setCopied] = useState(false);
  const [options, setOptions] = useState({
    includeHeader: true,
    includeBranding: true,
    includeAIMetadata: false,
  });

  if (!isOpen) return null;

  const wordCount = content.trim().split(/\s+/).filter(Boolean).length;
  const charCount = content.length;
  const estimatedPages = Math.max(1, Math.ceil(wordCount / 400));

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      console.error("Failed to copy to clipboard");
    }
  };

  const handleDownload = () => {
    if (selectedFormat === "clipboard") {
      handleCopyToClipboard();
      return;
    }

    if (selectedFormat === "markdown") {
      const blob = new Blob([content], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title.toLowerCase().replace(/\s+/g, "-")}.md`;
      a.click();
      URL.revokeObjectURL(url);
      return;
    }

    // PDF and DOCX would require server-side generation in production
    console.log(`Exporting as ${selectedFormat}...`);
    alert(`${selectedFormat.toUpperCase()} export would be generated server-side in production.`);
  };

  const selectedFormatData = formats.find((f) => f.id === selectedFormat)!;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-[#233D4C] rounded-xl border border-[#2a4a5c] w-full max-w-lg shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#2a4a5c]">
          <div>
            <h2 className="text-white text-lg font-semibold">Export</h2>
            <p className="text-gray-400 text-sm mt-0.5">Choose format and options</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-[#02182B] rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Format Selection */}
        <div className="p-6 border-b border-[#2a4a5c]">
          <h3 className="text-white text-sm font-medium mb-3">Format</h3>
          <div className="grid grid-cols-2 gap-3">
            {formats.map((format) => (
              <button
                key={format.id}
                onClick={() => setSelectedFormat(format.id)}
                className={`flex items-start gap-3 p-4 rounded-lg border-2 text-left transition-all ${
                  selectedFormat === format.id
                    ? "border-[#FD802E] bg-[#FD802E]/10"
                    : "border-[#2a4a5c] bg-[#02182B] hover:border-[#FD802E]/30"
                }`}
              >
                <format.icon
                  size={20}
                  className={selectedFormat === format.id ? "text-[#FD802E]" : "text-gray-400"}
                />
                <div>
                  <div className="text-white text-sm font-medium">{format.label}</div>
                  <div className="text-gray-500 text-xs mt-0.5">{format.description}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Preview Stats */}
        <div className="px-6 py-4 border-b border-[#2a4a5c] bg-[#02182B]/50">
          <h3 className="text-white text-sm font-medium mb-3">Preview</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-white font-bold text-xl">{wordCount.toLocaleString()}</div>
              <div className="text-gray-500 text-xs mt-1">Words</div>
            </div>
            <div className="text-center">
              <div className="text-white font-bold text-xl">{charCount.toLocaleString()}</div>
              <div className="text-gray-500 text-xs mt-1">Characters</div>
            </div>
            <div className="text-center">
              <div className="text-white font-bold text-xl">~{estimatedPages}</div>
              <div className="text-gray-500 text-xs mt-1">Est. pages</div>
            </div>
          </div>
        </div>

        {/* Export Options */}
        <div className="p-6 border-b border-[#2a4a5c]">
          <h3 className="text-white text-sm font-medium mb-3">Options</h3>
          <div className="space-y-3">
            {[
              { key: "includeHeader", label: "Include header", description: "Add title and metadata at the top" },
              { key: "includeBranding", label: "Include branding", description: "Add Searchline Engine logo and footer" },
              { key: "includeAIMetadata", label: "Include AI metadata", description: "Show AI model, confidence scores, and generation date" },
            ].map((opt) => (
              <label key={opt.key} className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={options[opt.key as keyof typeof options]}
                  onChange={(e) => setOptions({ ...options, [opt.key]: e.target.checked })}
                  className="w-4 h-4 mt-0.5 rounded border-gray-600 bg-[#02182B] text-[#FD802E] focus:ring-[#FD802E] focus:ring-offset-0"
                />
                <div>
                  <div className="text-gray-300 text-sm font-medium group-hover:text-white transition-colors">{opt.label}</div>
                  <div className="text-gray-500 text-xs">{opt.description}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="p-6 flex items-center gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2.5 bg-[#02182B] text-white rounded-lg hover:bg-[#0a2438] transition-colors font-medium text-sm"
          >
            Cancel
          </button>

          {selectedFormat === "clipboard" ? (
            <button
              onClick={handleCopyToClipboard}
              className={`flex-1 flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                copied
                  ? "bg-green-500 text-white"
                  : "bg-[#FD802E] text-[#233D4C] hover:bg-[#ff8f45]"
              }`}
            >
              {copied ? (
                <><Check size={16} />Copied!</>
              ) : (
                <><Clipboard size={16} />Copy to Clipboard</>
              )}
            </button>
          ) : (
            <button
              onClick={handleDownload}
              className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 bg-[#FD802E] text-[#233D4C] rounded-lg hover:bg-[#ff8f45] transition-colors font-semibold text-sm"
            >
              <Download size={16} />
              Download {selectedFormatData.ext}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
